import { Menu, X, LayoutDashboard, CalendarCheck, LayoutList, Inbox, Network, SlidersHorizontal } from "lucide-react"
import LOGO from "../assets/logo_h.svg"
import { useNavigate, useLocation } from "react-router-dom"
import { useState, useCallback } from "react"
import { useUnreadCount } from "../contexts/UnreadContext"

// ─── Static — defined once, never changes ────────────────────────────────────

const MAIN_ITEMS = [
  { id: "overview",     label: "Overview",      icon: LayoutDashboard,    route: "/profile" },
  { id: "interviews",   label: "Interviews",    icon: CalendarCheck,      route: "/interviews" },
  { id: "jobListings",  label: "Jobs",          icon: LayoutList,         route: "/jobs" },
  { id: "inbox",        label: "Inbox",         icon: Inbox,              route: "/inbox" },
]

const MANAGE_ITEMS = [
  { id: "organization", label: "Organisation",  icon: Network,            route: "/organisation" },
  { id: "settings",     label: "Settings",      icon: SlidersHorizontal,  route: "/settings" },
]

const getActiveTab = (pathname) => {
  if (pathname === "/profile" || pathname === "/") return "overview"
  if (pathname === "/interviews") return "interviews"
  if (pathname === "/inbox" || pathname.startsWith("/inbox/")) return "inbox"
  if (pathname === "/jobs" || pathname.startsWith("/jobs/")) return "jobListings"
  if (pathname === "/organisation") return "organization"
  if (pathname === "/settings") return "settings"
  return "overview"
}

const EUCLID = "'Euclid Circular A', sans-serif"

const SectionLabel = ({ label }) => (
  <p className="px-3 pt-5 pb-3 text-xs font-medium text-gray-500 tracking-wide" style={{ fontFamily: EUCLID }}>
    {label}
  </p>
)

const NavItem = ({ item, active, badge, onNavigate }) => (
  <button
    onClick={() => onNavigate(item.route)}
    className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors duration-150 ${
      active
        ? "bg-[#dfdfdf] text-gray-900"
        : "text-gray-700 hover:bg-white/60 hover:text-gray-900"
    }`}
  >
    <div className="relative w-5 h-5 mr-3 flex items-center justify-center flex-shrink-0">
      <item.icon size={16} className={active ? "text-gray-900" : "text-gray-500"} strokeWidth={1.5} />
      {badge != null && (
        <span
          className="absolute flex items-center justify-center rounded-full bg-[#E24B4A] text-white"
          style={{
            top: "-4px",
            right: "-5px",
            fontSize: "8px",
            fontWeight: 700,
            minWidth: "12px",
            height: "12px",
            padding: "0 2px",
            lineHeight: 1,
          }}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </div>
    <span
      className="flex-1"
      style={{
        fontFamily: EUCLID,
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "20px",
        color: "rgb(40, 40, 40)",
      }}
    >
      {item.label}
    </span>
  </button>
)

// ─────────────────────────────────────────────────────────────────────────────

const ProfileSidebar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Comes from context — no local polling, no flicker on remount
  const unreadCount = useUnreadCount()

  const activeTab = getActiveTab(location.pathname)

  const handleNavigation = useCallback((route) => {
    navigate(route)
    setIsMobileMenuOpen(false)
  }, [navigate])

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-gray-100 border-b border-gray-200 z-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
          >
            {isMobileMenuOpen ? (
              <X size={20} className="text-gray-700" />
            ) : (
              <Menu size={20} className="text-gray-700" />
            )}
          </button>
          <button onClick={() => handleNavigation("/")} className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-black rounded-md flex items-center justify-center">
              <img className="w-4 h-4 brightness-0 invert" src={LOGO} alt="Logo" />
            </div>
            <span className="text-base font-semibold text-gray-900">Resolut</span>
          </button>
        </div>
      </div>

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`h-screen w-60 flex flex-col fixed top-0 left-0 z-50 transition-transform duration-300 ease-in-out md:translate-x-0 ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full bg-[#f3f3f3]">
          {/* Header */}
          <div className="px-4 py-4 flex items-center justify-between">
            <button
              onClick={() => handleNavigation("/")}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-7 h-7 bg-black rounded-md flex items-center justify-center flex-shrink-0">
                <img className="w-4 h-4 brightness-0 invert" src={LOGO} alt="Logo" />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-base font-semibold text-gray-900">Resolut</span>
                <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l4-4 4 4M8 15l4 4 4-4" />
                </svg>
              </div>
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="md:hidden p-1.5 hover:bg-gray-200 rounded-md transition-colors"
            >
              <X size={18} className="text-gray-600" />
            </button>
          </div>

          {/* Navigation */}
          <div className="flex-1 px-3 overflow-y-auto">
            <SectionLabel label="Create" />
            <nav className="space-y-0.5">
              {MAIN_ITEMS.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  active={activeTab === item.id}
                  badge={item.id === "inbox" && unreadCount > 0 ? unreadCount : null}
                  onNavigate={handleNavigation}
                />
              ))}
            </nav>

            <SectionLabel label="Manage" />
            <nav className="space-y-0.5">
              {MANAGE_ITEMS.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  active={activeTab === item.id}
                  badge={null}
                  onNavigate={handleNavigation}
                />
              ))}
            </nav>

          </div>

          {/* Bottom toggle icon */}
          <div className="px-3 py-3">
            <button className="p-1.5 rounded-md hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default ProfileSidebar